import { ElementType, ReactNode, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { InputBaseComponentProps } from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

export type SearchFilterOption = {
	value: string;
	label: string;
	placeholder?: string;
	normalize?: (term: string) => string;
	isValid?: (term: string) => boolean;
	invalidMessage?: string;
	mask?: ElementType;
	render?: (props: {
		term: string;
		onTermChange: (term: string) => void;
	}) => ReactNode;
};

type SearchWithFilterProps = {
	options: SearchFilterOption[];
	type: string;
	term: string;
	onTypeChange: (type: string) => void;
	onTermChange: (term: string) => void;
};

export function isSearchTermValid(
	option: SearchFilterOption | undefined,
	term: string,
): boolean {
	if (!term || !option?.isValid) return true;
	return option.isValid(term);
}

export default function SearchWithFilter({
	options,
	type,
	term,
	onTypeChange,
	onTermChange,
}: SearchWithFilterProps): JSX.Element {
	const selected = options.find((option) => option.value === type);
	const fieldRef = useRef<HTMLDivElement>(null);
	const [focusAfterTypeChange, setFocusAfterTypeChange] = useState(false);

	const focusField = () =>
		fieldRef.current?.querySelector<HTMLElement>("input, button")?.focus();

	useEffect(() => {
		if (!focusAfterTypeChange) return;
		focusField();
	}, [type, focusAfterTypeChange]);

	const invalid = !isSearchTermValid(selected, term);

	return (
		<Stack
			direction={{ xs: "column", sm: "row" }}
			spacing={{ xs: 1, sm: 2 }}
			sx={{ width: "100%" }}
		>
			<TextField
				select
				variant="standard"
				label="Filtrar por"
				value={type}
				onChange={(event) => {
					setFocusAfterTypeChange(true);
					onTypeChange(event.target.value);
					onTermChange("");
				}}
				sx={{ minWidth: { xs: "100%", sm: 200 } }}
				SelectProps={{
					displayEmpty: true,
					MenuProps: { disableRestoreFocus: true },
				}}
				InputLabelProps={{ shrink: true }}
			>
				{options.map((option) => (
					<MenuItem key={option.value} value={option.value}>
						{option.label}
					</MenuItem>
				))}
			</TextField>

			<Box ref={fieldRef} sx={{ flex: 1 }}>
				{selected?.render ? (
					selected.render({ term, onTermChange })
				) : (
					<TextField
						fullWidth
						variant="standard"
						id="input-search"
						label="Pesquisar"
						value={term}
						placeholder={selected?.placeholder}
						onChange={(event) => {
							const value = event.target.value;
							onTermChange(
								selected?.normalize ? selected.normalize(value) : value,
							);
						}}
						error={invalid}
						helperText={invalid ? selected?.invalidMessage : undefined}
						InputProps={{
							inputComponent: selected?.mask as
								ElementType<InputBaseComponentProps> | undefined,
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
							endAdornment: term ? (
								<InputAdornment position="end">
									<IconButton
										size="small"
										aria-label="Limpar pesquisa"
										onClick={() => {
											onTermChange("");
											focusField();
										}}
									>
										<CloseIcon fontSize="small" />
									</IconButton>
								</InputAdornment>
							) : undefined,
						}}
					/>
				)}
			</Box>
		</Stack>
	);
}
